import {useMemo} from 'react';

import {useShopperInventoryIds} from '@amblique/king-living/app/hooks/use-shopper-inventory-ids';
import {useDepositPercentage} from '@amblique/king-living/app/hooks/use-deposit-percentage';
import {resolveEntityAts} from '@amblique/king-living/app/utils/inventory-utils';
import {
  EXCLUDED_FROM_DEPOSIT_ATTR,
  IN_STOCK_SENTINEL_ATS,
} from '@amblique/king-living/app/pages/checkout/util/payment-amount-utils';

/**
 * Drives the PDP "{percentage} deposit from {amount}" line (design: under the
 * "Total package value" row, above the payment logos).
 *
 * Mirrors the checkout Payment Amount rules (see `payment-amount-utils.js`) so a
 * shopper sees a consistent deposit story PDP → checkout:
 *   - In-stock = ATS > 0 AND ATS !== 999999 (the SFCC "orderable but no on-hand
 *     stock" sentinel). The deposit line only shows when the active product is
 *     NOT in stock (made-to-order / backorder), matching the checkout section
 *     that hides once any main item is genuinely on-hand.
 *   - Eligible = the active entity is not flagged `c_isExcludedFromDeposit`.
 *   - Amount = configured deposit rate (`depositPercentage` site preference) ×
 *     the post-promotion sale total — the same "deposit on discounted price"
 *     basis the checkout deposit math uses (`priceAfterItemDiscount`).
 *
 * DATA SOURCE — `entity` MUST be the FULL SCAPI product doc for the active entity
 * (the selected variant for a master-variant product, otherwise the product). The
 * PDP already fetches this exact document when a variant is selected — the page
 * `useProduct` (pid = variant) for plain Simple products, and the
 * `usePdpInventoryGate` `productsById[primaryProductId]` fetch
 * (`/products?ids=<variant>`) for master-with-covers / Complex. Callers thread
 * that resolved doc in here, so this hook does NO fetch of its own. Reading the
 * flag off a stripped `c_allVariantsData` entry would silently treat every variant
 * as deposit-eligible, which is why we require the full doc instead.
 *
 * @param {Object} params
 * @param {Object|null|undefined} params.entity - Full SCAPI doc for the active entity (carries `inventory` + `c_isExcludedFromDeposit`)
 * @param {number} params.saleTotal - Post-promotion sale total shown on the PDP
 * @param {boolean} [params.isLoading=false] - True while the authoritative doc is still resolving (suppresses the line)
 * @returns {{showDepositLine: boolean, depositAmount: number, formattedDepositPercentage: string|null}}
 */
export function usePdpDepositLine({entity, saleTotal, isLoading = false}) {
  const inventoryIds = useShopperInventoryIds();
  const {depositRate, isDepositPercentageValid, formattedDepositPercentage} = useDepositPercentage();

  return useMemo(() => {
    const ats = resolveEntityAts(entity, inventoryIds);

    // Checkout parity: ATS null / <= 0 / sentinel all count as "not in stock"
    // (deposit-eligible). Only a genuine on-hand count locks full payment.
    const isInStock =
      typeof ats === 'number' && Number.isFinite(ats) && ats > 0 && ats !== IN_STOCK_SENTINEL_ATS;

    const isExcludedFromDeposit = entity?.[EXCLUDED_FROM_DEPOSIT_ATTR] === true;

    const depositAmount =
      isDepositPercentageValid && Number.isFinite(saleTotal) && saleTotal > 0 ?
        saleTotal * depositRate :
        0;

    // Suppress while the authoritative doc is still resolving so a stale entity
    // can't flash the deposit line for a variant that is actually excluded.
    const showDepositLine =
      !isLoading &&
      !isInStock &&
      !isExcludedFromDeposit &&
      isDepositPercentageValid &&
      depositAmount > 0;

    return {showDepositLine, depositAmount, formattedDepositPercentage};
  }, [
    entity,
    inventoryIds,
    isLoading,
    depositRate,
    isDepositPercentageValid,
    formattedDepositPercentage,
    saleTotal,
  ]);
}
